import { useAppStore } from "@hooks/useAppStore";
import type { TTSPlayerRef } from "@memorang/features/src/TTS/types";
import type { BaseItemSchema } from "@memorang/types";
import type { AudioPlayerRef } from "@memorang/ui/components/AudioPlayerContainer";
import usePrepSequence from "@memorang/ui/components/StudySession/hooks/usePrepSequence";
import type { ItemAudioGate } from "@memorang/ui/components/StudySession/types";
import { type RefObject, useEffect, useRef } from "react";
import { resolveAudioGatePlan } from "../helpers/audio-gate-plan";
import { getItemAudioUri } from "../helpers/content";
import { getSummativeResponseConfig } from "../helpers/response-timing";
import useCurrentSession from "./useCurrentSession";
import useItemAnsweredState from "./useItemAnsweredState";
import useResponseControlsStore from "./useResponseControlsStore";
import useSessionStore from "./useSessionStore";

type ToeicItemAudioGate = ItemAudioGate & {
	ttsPlayerRef: RefObject<TTSPlayerRef | null>;
	ttsAutoPlay: boolean;
	ttsPlayOnce: boolean;
};

type Options = {
	ttsStem?: string;
};

const useItemAudioGate = (
	item: BaseItemSchema,
	isSummativeTest?: boolean,
	options?: Options,
): ToeicItemAudioGate | undefined => {
	const inReviewMode = useSessionStore((s) => s.inReviewMode);
	const showTimerConfig = useAppStore((s) => Boolean(s.sessionView?.timer));
	const isActiveSummative = Boolean(isSummativeTest) && !inReviewMode;
	const config = getSummativeResponseConfig(
		item.response,
		isActiveSummative || showTimerConfig,
	);
	const hasAudio = Boolean(getItemAudioUri(item.content));
	const { isAnswered } = useItemAnsweredState(item);
	const { currentItem } = useCurrentSession();
	const isCurrentItem = currentItem?.id === item.id;

	const itemId = item.id!;
	const audioCompleted = useResponseControlsStore((s) =>
		Boolean(s.audioCompletedItems[itemId]),
	);
	const markAudioComplete = useResponseControlsStore(
		(s) => s.markAudioComplete,
	);
	const playerRef = useRef<AudioPlayerRef | null>(null);
	const ttsPlayerRef = useRef<TTSPlayerRef | null>(null);

	const {
		audioAutoPlays,
		ttsAutoPlay,
		ttsPlayOnce,
		forceCompleted,
		nothingPlays,
		playOnce,
	} = resolveAudioGatePlan({
		config,
		hasAudio,
		isFirstStimulusItem: item.currentStimulusItemIndex === 1,
		ttsStem: options?.ttsStem,
		isActiveSummative,
	});

	usePrepSequence({
		prepTime: config?.prepTime,
		hasAudioStimulus: audioAutoPlays,
		hasRecorder: false,
		audioPlayerRef: playerRef,
		hasTtsStimulus: ttsAutoPlay,
		ttsPlayerRef,
		onAudioEnded: () => markAudioComplete(itemId),
		enabled:
			isCurrentItem &&
			(audioAutoPlays || ttsAutoPlay) &&
			!audioCompleted &&
			!isAnswered,
		waitForPlayer: true,
	});

	useEffect(() => {
		if (nothingPlays && !audioCompleted) {
			markAudioComplete(itemId);
		}
	}, [nothingPlays, audioCompleted, markAudioComplete, itemId]);

	useEffect(() => {
		if (isAnswered) {
			playerRef.current?.pause();
			ttsPlayerRef.current?.pause();
		}
	}, [isAnswered]);

	if (!(hasAudio || ttsAutoPlay)) {
		return undefined;
	}

	return {
		playerRef,
		playOnce,
		onEnd: () => {
			if (ttsAutoPlay) {
				return;
			}
			markAudioComplete(itemId);
		},
		forceCompleted,
		ttsPlayerRef,
		ttsAutoPlay,
		ttsPlayOnce,
	};
};

export default useItemAudioGate;
